import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TaskService } from '@shared/services/task.service';
import * as CryptoJS from 'crypto-js';
import { isString } from 'lodash';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';
import { urlEndPoints } from '../../shared/config/end-points';
import { User } from '../../shared/models/user.model';
import { UserService } from '../../shared/services/user.service';
import { Token } from '../models/token.model';
import { ClearService } from './clear.service';
import { MonitoringService } from './monitoring.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private httpClient: HttpClient,
    private storageService: StorageService,
    private clearService: ClearService,
    private MonitoringService: MonitoringService,
    private userService: UserService,
    private taskService: TaskService
  ) {}

  isAuthenticated(): boolean | undefined {
    const token = this.storageService.getToken();
    return (
      isString(this.storageService.getAccessToken()) &&
      token &&
      token.expiredAt &&
      new Date(token.expiredAt) > new Date()
    );
  }

  logIn(user: User): Observable<any> {
    return this.httpClient.post(urlEndPoints.login, user).pipe(
      map((result: any) => {
        if (result && !result.requiresOtp) {
          this.processLoginResult(result);
        }
        return result;
      })
    );
  }

  verifyOtp(code: string, twoFactorCookie: string): Observable<any> {
    return this.userService.verifyOtp(code, twoFactorCookie).pipe(
      map((result: any) => {
        this.processLoginResult(result);
      })
    );
  }

  resendOtp(twoFactorCookie: string): Observable<any> {
    return this.userService.resendOtp(twoFactorCookie);
  }

  private processLoginResult(result: any): void {
    const token: Token = {
      access_token: result.token,
      expiredAt: result.expiredAt,
    };
    this.storageService.storeToken(token);
    const user: User = result.doc as User;
    user.profile = result.doc.profile;
    this.storageService.storeUser(result.doc as User);
    this.storageService.saveData('selectedHospitalsId', [user?.profile?.principalHospitalId ?? '']);
    this.storageService.saveData('principalHospitalId', user?.profile?.principalHospitalId ?? '');

    result.doc.persona.accessOptionsHash = CryptoJS.SHA256(JSON.stringify(result.doc.persona.accessOptions)).toString();

    this.storageService.saveData('reverseOrderName', result.reverseOrderName);
    this.taskService.triggerReload();
  }

  logout(): void {
    this.clearService.clearAll();
    this.MonitoringService.clearUserContext();
  }

  resetPassword(body: any): Observable<any> {
    return this.httpClient.post(urlEndPoints.sendEmail, body);
  }

  changePassword(body: any): Observable<any> {
    return this.httpClient.post(urlEndPoints.changePassword, body);
  }
}
